import styled from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

/* ═══════════════════════════════════════════
   1. SUB-VERTICALS GRID
   ═══════════════════════════════════════════ */

export const StyledSubVerticalsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 40px;

  @media screen and (min-width: ${CSSVars.breakpointSM}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledSubVerticalCard = styled.div`
  background: ${CSSVars.white};
  border: 1.5px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 24px;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  text-align: center;

  &:hover {
    border-color: var(--accent, ${CSSVars.blue500});
    box-shadow: 0 6px 24px var(--accent-light, rgba(76, 79, 255, 0.1));
    transform: translateY(-2px);
  }

  .sv-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--accent-light, rgba(76, 79, 255, 0.08));
    color: var(--accent, ${CSSVars.blue500});
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;

    svg { width: 24px; height: 24px; }
  }

  .sv-name {
    font-size: 15px;
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 6px;
  }

  .sv-desc {
    font-size: 13px;
    color: ${CSSVars.gray600};
    line-height: 1.55;
    font-weight: 300;
    margin-bottom: 14px;
  }
`;

export const StyledWidgetTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
`;

export const StyledWidgetTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 100px;
  background: var(--accent-light, rgba(76, 79, 255, 0.08));
  color: var(--accent, ${CSSVars.blue500});
`;

/* ═══════════════════════════════════════════
   2. TESTIMONIALS
   ═══════════════════════════════════════════ */

export const StyledTestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 40px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledTestimonialCard = styled.div`
  background: ${CSSVars.white};
  border: 1.5px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

  .quote-mark {
    font-size: 32px;
    line-height: 1;
    color: var(--accent, ${CSSVars.blue500});
    opacity: 0.4;
    font-family: Georgia, serif;
  }

  .quote-text {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray700};
    line-height: 1.7;
    font-weight: 400;
    font-style: italic;
    flex: 1;
  }

  .quote-stars {
    display: flex;
    gap: 2px;
    color: #f59e0b;
    font-size: 14px;
  }

  .quote-author {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .author-name {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    color: ${CSSVars.black};
  }

  .author-role {
    font-size: ${CSSVars.fontSizeXS};
    color: ${CSSVars.gray500};
    font-weight: 400;
  }
`;

/* ═══════════════════════════════════════════
   3. HOW IT WORKS
   ═══════════════════════════════════════════ */

export const StyledStepsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-top: 48px;
  position: relative;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(3, 1fr);

    &::before {
      content: '';
      position: absolute;
      top: 27px;
      left: 18%;
      right: 18%;
      height: 2px;
      background: linear-gradient(
        90deg,
        var(--accent-light, rgba(76, 79, 255, 0.2)),
        var(--accent, ${CSSVars.blue500}),
        var(--accent-light, rgba(76, 79, 255, 0.2))
      );
      opacity: 0.5;
    }
  }
`;

export const StyledStep = styled.div`
  text-align: center;

  .step-num {
    width: 54px;
    height: 54px;
    background: var(--accent, ${CSSVars.blue500});
    color: ${CSSVars.white};
    font-size: 22px;
    font-weight: 900;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 18px;
    box-shadow: 0 4px 14px var(--accent-light, rgba(76, 79, 255, 0.35));
    position: relative;
    z-index: 1;
  }

  .step-title {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 800;
    color: ${CSSVars.black};
    margin-bottom: 8px;
  }

  .step-desc {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.6;
    font-weight: 300;
    max-width: 280px;
    margin: 0 auto;

    a {
      color: var(--accent, ${CSSVars.blue500});
      font-weight: 600;
      text-decoration: none;
      &:hover { text-decoration: underline; }
    }
  }
`;

/* ═══════════════════════════════════════════
   3b. PLATFORM SECTION
   ═══════════════════════════════════════════ */

export const StyledPlatformCarousel = styled.div`
  overflow: hidden;
  margin-top: 36px;
  position: relative;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40px;
    z-index: 1;
    pointer-events: none;
    @media screen and (min-width: ${CSSVars.breakpointMD}) { width: 80px; }
  }
  &::before { left: 0; background: linear-gradient(90deg, ${CSSVars.gray50} 0%, transparent 100%); }
  &::after { right: 0; background: linear-gradient(270deg, ${CSSVars.gray50} 0%, transparent 100%); }

  .carousel-track {
    display: flex;
    align-items: center;
    gap: 32px;
    @media screen and (min-width: ${CSSVars.breakpointMD}) { gap: 48px; }
    width: max-content;
    animation: scrollPlatforms 30s linear infinite;
  }

  .logo-item {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    opacity: 0.55;
    transition: opacity 0.3s;
    &:hover { opacity: 1; }
    img { height: 36px; width: auto; object-fit: contain; }
  }

  @keyframes scrollPlatforms {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

export const StyledPlatformCta = styled.div`
  text-align: center;
  margin-top: 32px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    color: var(--accent, ${CSSVars.blue500});
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

export const StyledStepsCta = styled.div`
  text-align: center;
  margin-top: 44px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 16px 32px;
    background: var(--accent, ${CSSVars.blue500});
    color: ${CSSVars.white};
    border-radius: 8px;
    font-weight: 700;
    font-size: ${CSSVars.fontSizeMD};
    text-decoration: none;
    transition: filter 0.2s, transform 0.2s;
    box-shadow: 0 4px 14px rgba(76, 79, 255, 0.25);
    &:hover { filter: brightness(1.15); transform: translateY(-1px); }
  }
`;

/* ═══════════════════════════════════════════
   4. CTA BAND
   ═══════════════════════════════════════════ */

export const StyledCtaBand = styled.section`
  background: linear-gradient(135deg, #09093a 0%, var(--accent, ${CSSVars.blue500}) 150%);
  padding: 72px 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -5%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.06), transparent 65%);
    pointer-events: none;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 80px 30px;
  }
`;

export const StyledCtaBandInner = styled.div`
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  position: relative;

  .cta-badge {
    display: inline-block;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 100px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 700;
    color: ${CSSVars.white};
    margin-bottom: 24px;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 900;
    color: ${CSSVars.white};
    margin-bottom: 14px;
    line-height: 1.2;
    @media screen and (min-width: ${CSSVars.breakpointMD}) { font-size: 2.25rem; }
  }

  .cta-sub {
    font-size: 17px;
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 32px;
    line-height: 1.6;
    font-weight: 300;
  }

  .cta-buttons {
    display: flex;
    gap: 14px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .cta-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 16px 32px;
    background: ${CSSVars.white};
    color: var(--accent, ${CSSVars.blue500});
    border-radius: 8px;
    font-weight: 700;
    font-size: 16px;
    text-decoration: none;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
    transition: transform 0.2s;
    &:hover { transform: translateY(-2px); }
  }

  .cta-secondary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 16px 32px;
    background: rgba(255, 255, 255, 0.12);
    color: ${CSSVars.white};
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    font-weight: 700;
    font-size: 16px;
    text-decoration: none;
    transition: background 0.2s;
    &:hover { background: rgba(255, 255, 255, 0.2); }
  }

  .cta-note {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 18px;
    font-weight: 300;
  }
`;

/* ═══════════════════════════════════════════
   5. SHARED SECTION PRIMITIVES (reused from VerticalHub)
   ═══════════════════════════════════════════ */

export const StyledSection = styled.section<{ $bg?: string }>`
  padding: 64px 20px;
  background: ${({ $bg }) => $bg || CSSVars.white};

  @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 80px 30px; }
  @media screen and (min-width: ${CSSVars.breakpointLG}) { padding: 96px 40px; }
`;

export const StyledInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

export const StyledEyebrow = styled.p`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--accent, ${CSSVars.blue500});
  margin-bottom: 12px;
`;

export const StyledSectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.15;
  color: ${CSSVars.black};
  margin-bottom: 16px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { font-size: ${CSSVars.fontSize2XL}; }
  @media screen and (min-width: ${CSSVars.breakpointLG}) { font-size: 2.625rem; }
`;

export const StyledSectionSub = styled.p<{ $center?: boolean }>`
  font-size: ${CSSVars.fontSizeMD};
  color: ${CSSVars.gray600};
  line-height: 1.7;
  font-weight: 300;
  max-width: 600px;
  margin-top: 8px;
  ${({ $center }) => $center && 'margin-left: auto; margin-right: auto;'}

  @media screen and (min-width: ${CSSVars.breakpointMD}) { font-size: 17px; }
`;
